import { lazy } from 'react';
import type { ComponentType } from 'react';

// Recovering from a chunk that is no longer on the server.
//
// Every route here is code-split, so navigating fetches a JS chunk whose
// filename carries a content hash. A deploy replaces those files. Two
// things follow, and the second is the one that actually hurts:
//
//   1. DURING a rolling deploy, index.html can come from an instance on
//      the new build while the chunk request lands on one still serving
//      the old — the file 404s. This window is a minute or two and
//      closes on its own.
//
//   2. AFTER a deploy, any tab that was already open is holding an
//      index.html naming hashes that no longer exist anywhere. The next
//      navigation to a not-yet-loaded route 404s, and keeps 404ing —
//      that tab is broken until someone thinks to hard-refresh. No
//      infrastructure change fixes this one; the stale document is
//      already in the user's browser.
//
// The fix is a reload, because a reload fetches a fresh index.html and
// with it the current hashes. The care needed is in NOT doing it
// forever: if a chunk is genuinely broken rather than merely stale, an
// unguarded reload-on-failure is an infinite refresh loop, which is a
// considerably worse bug than the one being fixed.

/** Where the last recovery attempt is remembered. */
const RELOAD_STAMP_KEY = 'tidee.chunkReloadAt';

/**
 * How long after a recovery reload we refuse to try again.
 *
 * Long enough that a genuinely broken chunk cannot loop — the second
 * failure falls through to the error boundary, which shows the user
 * something rather than flickering — and short enough that a user who
 * hits a second, unrelated deploy an hour later is still rescued.
 *
 * The stamp is deliberately NOT cleared when a chunk loads successfully,
 * which was the first version of this and looped: a page load fetches
 * several chunks, so the ones that succeed wiped the guard before the
 * failing one consulted it — 90 reloads in ten seconds. One chunk
 * loading says nothing about another being fixed. The stamp records
 * "a recovery was attempted at T" and only time clears it.
 */
export const RELOAD_COOLDOWN_MS = 60_000;

/**
 * May we reload to recover, given when we last did?
 *
 * Split out and exported because this is the whole safety argument, and
 * it is the part worth testing: everything else is browser plumbing.
 */
export const shouldReload = (
    lastReloadAt: number | null,
    now: number
): boolean => {
    if (lastReloadAt === null) return true;
    // A stamp from the future means a clock change, not a recent
    // reload. Treating it as recent would strand the tab for as long as
    // the clock is wrong.
    if (lastReloadAt > now) return true;
    return now - lastReloadAt > RELOAD_COOLDOWN_MS;
};

// sessionStorage rather than localStorage: the stamp is about THIS tab's
// document, and it should not outlive the tab or leak into others.
// Every access is guarded — private modes and locked-down browsers throw
// on the accessor itself, and a storage failure must not turn a
// recoverable chunk error into an unrecoverable one.
const readStamp = (): number | null => {
    try {
        const raw = window.sessionStorage.getItem(RELOAD_STAMP_KEY);
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeStamp = (value: number): void => {
    try {
        window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(value));
    } catch {
        // No storage means no loop guard, so we do not reload at all.
        // Handled by the caller checking this returned nothing useful —
        // see the throw in the catch below.
    }
};

// Mirrors React's own lazy() signature, which is generic over
// ComponentType<any> — anything narrower rejects real page components,
// since each has its own props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

type Factory<T> = () => Promise<{ default: T }>;

/**
 * `lazy`, but a chunk that will not load reloads the page once.
 *
 * Drop-in for React.lazy — same signature, same return type.
 */
export const lazyWithRecovery = <T extends AnyComponent>(factory: Factory<T>) =>
    lazy(() =>
        factory().catch((firstError) =>
            // One retry before anything drastic. A flaky connection
            // is far more common than a redeploy, and a retry costs
            // one request where a reload costs the whole document
            // and any unsaved state in it.
            factory().catch((error) => {
                if (!shouldReload(readStamp(), Date.now())) throw error;
                writeStamp(Date.now());
                // If the stamp did not stick, we have no loop guard,
                // and looping is worse than failing visibly.
                if (readStamp() === null) throw error;

                window.location.reload();
                // Never resolves — deliberately. The document is on
                // its way out, and rejecting here would flash the
                // error boundary at the user on the way.
                // eslint-disable-next-line compat/compat -- Opera Mini isn't a supported target
                return new Promise<{ default: T }>(() => {
                    void firstError;
                });
            })
        )
    );
